import { useEffect, useState } from 'react';

import { getCurrentPartner } from '@src/providers/dataProvider/shared';

import {
  DEFAULT_AVAILABILITIES,
  DEFAULT_CHECKIN_AVAILABILITIES,
  DEFAULT_CONDITIONS,
  DEFAULT_REJECT_REASONS,
  DEFAULT_STATES,
} from './constants';
import { fetchInventoryConstants } from './fetchInventoryItems';
import type { InventoryConstants } from './types';

const FALLBACK: InventoryConstants = {
  states: DEFAULT_STATES,
  conditions: DEFAULT_CONDITIONS,
  rejectReasons: DEFAULT_REJECT_REASONS,
  availabilities: DEFAULT_AVAILABILITIES,
  checkinAvailabilities: DEFAULT_CHECKIN_AVAILABILITIES,
  locationTypes: [],
  wmsStatuses: [],
  warehouses: [],
  departments: [],
  categories: [],
};

// Keyed by partner since conditions, warehouses, and facets are partner-scoped.
let cache: { partnerId: number, constants: InventoryConstants } | null = null;

/**
 * Test-only. The cache is module state, so without this every test in a file
 * inherits whatever the previous one left behind and the suite becomes
 * order-dependent.
 */
export const resetInventoryConstantsCache = (): void => {
  cache = null;
};

/**
 * Item states, condition grades, reject reasons, and the remaining filter
 * option lists. Serves sensible defaults immediately, refined from the API
 * once available. `isFallback` is true when the live fetch failed (we're
 * stuck on defaults) or only partly loaded (partner options missing) — callers
 * surface that so stale or empty options aren't mistaken for the real set.
 */
export const useInventoryConstants = (): InventoryConstants & { isFallback: boolean } => {
  // getCurrentPartner() (not requirePartner()) because this runs at render
  // time; a missing partner surfaces as the data provider's error instead.
  const partnerId = getCurrentPartner()?.id;
  const cached = cache && cache.partnerId === partnerId ? cache.constants : null;
  const [constants, setConstants] = useState<InventoryConstants>(cached ?? FALLBACK);
  const [isFallback, setIsFallback] = useState(false);

  useEffect(() => {
    if (cached) return;
    // No partner means no fetch, so the hardcoded defaults are all we have —
    // flag it, or the page presents them as the partner's real option set.
    if (partnerId == null) {
      setIsFallback(true);
      return;
    }
    let cancelled = false;
    fetchInventoryConstants(partnerId)
      .then(({ isPartial, ...result }) => {
        // Don't cache a partial load, so the next mount retries it.
        if (!isPartial) cache = { partnerId, constants: result };
        if (cancelled) return;
        setConstants(result);
        setIsFallback(isPartial);
      })
      .catch((error: unknown) => {
        console.error('Falling back to default inventory filter options', { partnerId, error });
        if (!cancelled) setIsFallback(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `cache` is module
    // state, deliberately not a dependency; `cached` is derived from it and partnerId
  }, [partnerId]);

  return { ...constants, isFallback };
};
